// NewtranxPlugin 国际化接入
// 移植自 new-xy-web/plugins/min.js：注入公司自有的 CDN 翻译插件，
// 对整页 DOM 在 zh/tw/en 之间做前端机器翻译，语言存于 localStorage.currentLang。
//
// 插件自带的切换器 UI 与本站导航风格不一致，且无干净的公开切换 API，
// 翻译逻辑绑在它生成的 .xykj-lang-li 元素的 click 事件上。因此这里：
//   1. 让插件把原生切换器渲染进一个屏幕外的隐藏容器（HIDDEN_CONTAINER_ID）；
//   2. 由 LanguageSwitcher 组件自绘与导航一致的 UI；
//   3. 切换语言时程序化触发隐藏 li 的 click，复用插件的翻译引擎。

const SCRIPT_SRC =
  'https://newtranx-cdn-new.oss-cn-beijing.aliyuncs.com/js/web-translate.min.js';

const STORAGE_KEY = 'currentLang';

// 沿用参考站的 appkey/secrt（占位）。如平台要求本域名单独注册，替换这两个值即可。
const api = {
  appkey: 'U01vfnH3',
  secrt: '3c711803795d6d417de967299e63df9b3077a120',
};

// 插件原生切换器渲染到的隐藏容器 id（不展示给用户）。
const HIDDEN_CONTAINER_ID = 'newtranx-native-host';

export type LangCode = 'zh' | 'tw' | 'en';

export const LANGS: { code: LangCode; label: string }[] = [
  { code: 'zh', label: '简体中文' },
  { code: 'tw', label: '繁體中文' },
  { code: 'en', label: 'English' },
];

let injected = false;
let plugin: NewtranxPluginInstance | null = null;

function ensureHiddenHost(): HTMLElement {
  let host = document.getElementById(HIDDEN_CONTAINER_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = HIDDEN_CONTAINER_ID;
    host.style.cssText =
      'position:absolute;left:-9999px;top:0;width:0;height:0;overflow:hidden;';
    document.body.appendChild(host);
  }
  return host;
}

function buildSettings(): NewtranxSettings {
  return {
    langs: LANGS.map((l) => l.code),
    srcLang: 'zh',
    container: HIDDEN_CONTAINER_ID,
    // 传非空 styles 把原生切换器强制移到屏幕外隐藏。
    // 注意：空对象会被插件判定为“未设置”而回退到 position:fixed（反而显示出来），
    // 因此这里必须给非空样式。
    styles: {
      position: 'fixed',
      left: '-9999px',
      top: '0',
      visibility: 'hidden',
    },
    pageAdjust: (lang: string) => {
      try {
        localStorage.setItem(STORAGE_KEY, lang);
      } catch {
        // 忽略 localStorage 不可用（隐私模式等）
      }
      window.dispatchEvent(
        new CustomEvent('newtranx:langchange', { detail: lang }),
      );
    },
  };
}

function startPlugin() {
  if (plugin || typeof window.NewtranxPlugin === 'undefined') return;
  ensureHiddenHost();
  plugin = new window.NewtranxPlugin();
  plugin.init(api, buildSettings());
}

/**
 * 注入翻译脚本并初始化插件。重复调用安全（模块级去重）。
 * 在应用挂载时调用一次即可。
 */
export function initNewtranx() {
  if (injected || typeof document === 'undefined') return;
  injected = true;

  if (typeof window.NewtranxPlugin !== 'undefined') {
    startPlugin();
    return;
  }

  const script = document.createElement('script');
  script.src = SCRIPT_SRC;
  script.async = true;
  script.onload = startPlugin;
  document.body.appendChild(script);
}

/** 读取当前语言（默认 zh）。 */
export function getCurrentLang(): LangCode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'zh' || v === 'tw' || v === 'en') return v;
  } catch {
    // 忽略
  }
  return 'zh';
}

/**
 * 切换语言：程序化触发插件隐藏切换器中对应语言项的 click，复用其翻译引擎。
 * 返回是否成功触发（插件尚未就绪时返回 false）。
 */
export function switchLang(lang: LangCode): boolean {
  const items = document.querySelectorAll<HTMLElement>(
    `#${HIDDEN_CONTAINER_ID} .xykj-lang-li`,
  );
  for (const li of items) {
    if (li.getAttribute('xykj-lang-data') === lang) {
      li.click();
      return true;
    }
  }
  return false;
}
